import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import CountrySelect from "../components/CountrySelect";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import { Globe2, User2, Building2 } from "lucide-react";

export default function Register() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "employer" ? "employer" : "candidate";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    country_code: "",
    company_name: "",
    registration_number: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);

  // Help label per country for the registration number field
  const REG_HINTS = {
    FR: "SIRET — 14 chiffres",
    BE: "BCE — 10 chiffres",
    CH: "IDE — CHE-XXX.XXX.XXX",
    GB: "Companies House — 8 caractères",
    DE: "Handelsregister — HRA/HRB + chiffres",
    IT: "Codice Fiscale / P.IVA — 11 chiffres",
    ES: "CIF — lettre + 8 chiffres",
    PT: "NIPC — 9 chiffres",
    NL: "KvK — 8 chiffres",
    US: "EIN — XX-XXXXXXX",
    CA: "BN — 9 chiffres",
    LU: "RCS — B + chiffres",
  };
  const regHint = REG_HINTS[form.country_code] || t("auth.reg_hint_generic");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.country_code) { toast.error(t("auth.select_country_first")); return; }
    setLoading(true);
    try {
      await register({ ...form, role, language: i18n.language });
      toast.success(t("auth.welcome_new"));
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.registration_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-slate-50/50" data-testid="register-page">
      <div className="w-full max-w-md mec-card p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center">
            <Globe2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">{t("auth.register_title")}</div>
            <div className="text-xs text-slate-500">{t("auth.register_sub")}</div>
          </div>
        </div>

        <Tabs value={role} onValueChange={setRole} className="mb-5">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="candidate" data-testid="tab-candidate">
              <User2 className="w-4 h-4 mr-1.5" /> {t("auth.candidate")}
            </TabsTrigger>
            <TabsTrigger value="employer" data-testid="tab-employer">
              <Building2 className="w-4 h-4 mr-1.5" /> {t("auth.employer")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="full_name" className="text-sm">{role === "employer" ? t("auth.contact_name") : t("auth.full_name")}</Label>
            <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="reg-fullname" />
          </div>
          {role === "employer" && (
            <>
              <div>
                <Label htmlFor="company_name" className="text-sm">{t("auth.company_name")}</Label>
                <Input id="company_name" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="reg-company" />
              </div>
              <div>
                <Label htmlFor="website" className="text-sm">{t("auth.website")}</Label>
                <Input id="website" type="url" required placeholder="https://www.entreprise.com" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="reg-website" />
              </div>
              <div>
                <Label htmlFor="registration_number" className="text-sm">{t("auth.registration_number")}</Label>
                <Input id="registration_number" required value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} data-testid="reg-registration" placeholder={regHint} />
                <p className="text-[11px] text-slate-500 mt-1">{regHint}</p>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email" className="text-sm">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm">{t("auth.password_min")}</Label>
            <PasswordInput id="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-testid="reg-password" />
          </div>
          <div>
            <Label className="text-sm">{t("auth.country")}</Label>
            <CountrySelect value={form.country_code} onChange={(v) => setForm({ ...form, country_code: v })} testId="reg-country" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#004EEB] hover:bg-[#003BCC] text-white mt-2" data-testid="reg-submit">
            {loading ? t("auth.registering") : t("auth.register_btn")}
          </Button>
          {role === "employer" && (
            <p className="text-[11px] text-slate-500 leading-relaxed mt-2 p-2 bg-slate-50 rounded border border-slate-200">
              🛡️ {t("auth.verification_notice")}
            </p>
          )}
        </form>

        <div className="mt-6 text-center text-sm text-slate-600">
          {t("auth.already_account")}{" "}
          <Link to="/login" className="text-[#004EEB] hover:underline font-medium" data-testid="link-login">{t("auth.login_btn")}</Link>
        </div>
      </div>
    </div>
  );
}
